import styled from "@emotion/styled"
import type {
	PRadioGroupProps,
	RadioGroupChangeEventDetail,
	TabsBarUpdateEventDetail,
} from "@porsche-design-system/components-react"
import {
	PButton,
	PInlineNotification,
	PRadioGroup,
	PRadioGroupOption,
	PTabsBar,
} from "@porsche-design-system/components-react"
import {
	breakpoint,
	spacingFluidMd,
	spacingFluidXl,
	spacingStaticMd,
} from "@porsche-design-system/components-react/emotion"
import type { PropsWithChildren } from "react"
import { useCallback } from "react"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { ButtonGroup, FormLayout } from "../form"
import { Grid } from "../grid/Grid"
import { GridItem } from "../grid/GridItem"
import { FinancingFormSkeleton } from "./FinancingFormSkeleton"
import type { FinancingNotification } from "./FinancingNotification"
import { Information } from "./Information"
import { mapInlineNotificationState } from "./mapInlineNotificationState"

interface StateLoading {
	onCancel?: () => void
	state: "loading"
}

interface StateNotification extends FinancingNotification {
	onCancel?: () => void
	state: "notification"
}

interface StateReady extends PropsWithChildren {
	isSubmitting: boolean

	onCancel?: () => void
	onProductChange?: (value: string) => void
	onProductTypeChange?: (value: string) => void

	onSave?: () => void
	product?: string
	products: {
		label: string
		value: string
	}[]

	productType: string
	productTypes: { label: string; value: string }[]
	state: "ready"
}

type FinancingFormProps = StateLoading | StateReady | StateNotification

const messages = defineMessages({
	noProductsAvailableText: {
		// eslint-disable-next-line formatjs/blocklist-elements
		defaultMessage: `Currently there is no {
			productType, select,
              LEASING {Leasing}
              THREEWAY_LOAN {Financing}
              RENTING {Renting}
              other {{productType}}
            } available for this vehicle`,
		id: "vehicleDetails.overview.financing.form.noProductsAvailable.text",
	},
	noProductsAvailableTitle: {
		// eslint-disable-next-line formatjs/blocklist-elements
		defaultMessage: `There is no {
			productType, select,
              LEASING {Leasing}
              THREEWAY_LOAN {Financing}
              RENTING {Renting}
              other {{productType}}
            } available.`,
		id: "vehicleDetails.overview.financing.form.noProductsAvailable.title",
	},
})

export const FinancingForm = (props: FinancingFormProps) => {
	const { onCancel, state } = props
	if (state === "notification") {
		const { severity, text, title } = props
		return (
			<FormLayout $width="half">
				<PInlineNotification
					state={mapInlineNotificationState(severity)}
					heading={title}
					description={text}
					dismissButton={false}
				/>
				{onCancel && (
					<ButtonGroup>
						<PButton type="button" variant="secondary" onClick={onCancel}>
							<FormattedMessage {...formMessages.cancel} />
						</PButton>
					</ButtonGroup>
				)}
			</FormLayout>
		)
	}

	if (state === "loading") {
		return <FinancingFormSkeleton />
	}

	if (state !== "ready") {
		return null
	}

	return <FinancingFormReady {...props} />
}

const FinancingFormReady = ({
	children,
	isSubmitting,
	onCancel,
	onProductChange,
	onProductTypeChange,
	onSave,
	product,
	products,
	productType,
	productTypes,
}: StateReady) => {
	const { formatMessage } = useIntl()
	const handleProductTypeChange = useCallback(
		(event: CustomEvent<TabsBarUpdateEventDetail>) => {
			const { activeTabIndex } = event.detail
			const nextProductType = productTypes[activeTabIndex]
			if (nextProductType.value === productType) {
				return
			}
			onProductTypeChange?.(nextProductType.value)
		},
		[productTypes, productType, onProductTypeChange],
	)

	const handleProductChange = useCallback(
		(event: CustomEvent<RadioGroupChangeEventDetail>) => {
			const { value } = event.target as HTMLElement & PRadioGroupProps
			onProductChange?.(value)
		},
		[onProductChange],
	)

	const productTypeIndex = productTypes.findIndex((it) => it.value === productType)

	return (
		<Container>
			<Grid $columnGap={{ base: spacingFluidMd, s: spacingFluidXl }} $rowGap={spacingFluidMd}>
				<GridItem size={12}>
					<PTabsBar background="surface" activeTabIndex={productTypeIndex} onUpdate={handleProductTypeChange}>
						{productTypes.map(({ label, value }) => (
							<button key={value} type="button">
								{label}
							</button>
						))}
					</PTabsBar>
				</GridItem>

				{products.length > 0 ? (
					<>
						<ProductSelect size={{ base: 12, m: 4 }}>
							<PRadioGroup name="product" onChange={handleProductChange} value={product}>
								{products.map(({ label, value }, i) => (
									<PRadioGroupOption key={i} label={label} value={value} />
								))}
							</PRadioGroup>
						</ProductSelect>

						<GridItem size={{ base: 12, m: 8 }}>{children}</GridItem>
					</>
				) : (
					<GridItem size={12}>
						<Information
							icon="information"
							title={formatMessage(messages.noProductsAvailableTitle, { productType })}
							text={formatMessage(messages.noProductsAvailableText, { productType })}
						/>
					</GridItem>
				)}
			</Grid>

			<ButtonGroup direction="row">
				{onSave && (
					<PButton loading={isSubmitting} onClick={onSave}>
						<FormattedMessage {...formMessages.save} />
					</PButton>
				)}
				{onCancel && (
					<PButton type="button" variant="secondary" onClick={onCancel}>
						<FormattedMessage {...formMessages.cancel} />
					</PButton>
				)}
			</ButtonGroup>
		</Container>
	)
}

const Container = styled.div`
	min-height: 25vh;
`

const ProductSelect = styled(GridItem)`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticMd};

	@media (min-width: ${breakpoint.m}px) {
		position: relative;

		&:after {
			content: '';
			position: absolute;
			top: 0;
			bottom: 0;
			inset-inline-end: calc(${spacingFluidXl} / -2);
			width: 1px;
			background: rgb(227, 228, 229);
		}
	}
`
